import {withStaticProperties} from '../utils/with-static-properties';
import {
  $HookForm,
  $FormActions,
  $FormRow,
  $ServerError,
  $FormSubmit,
  $FormContainer,
  $FieldArray,
} from './hook-form';
import {$CheckboxAdapter, $CheckboxAdapterProps} from './adapters/checkbox';
import {FieldValues} from 'react-hook-form';
import {
  AdapterProps,
  HookFormFieldFactory,
  RenderFieldProps,
} from './field-factory';
import {ReactElement} from 'react';
import {
  $CheckboxGroupAdapter,
  $CheckboxGroupAdapterProps,
  $CheckboxGroupItemAdapter,
} from './adapters/checkbox-group';
import {$ChipAdapter, $ChipAdapterProps} from './adapters/chip';
import {
  $ChipGroupAdapter,
  $ChipGroupAdapterProps,
  $ChipGroupItemAdapter,
} from './adapters/chip-group';
import {
  $ColorPickerAdapter,
  $ColorPickerAdapterProps,
} from './adapters/color-picker';
import {
  $CurrencyInputAdapter,
  $CurrencyInputAdapterProps,
} from './adapters/currency-input';
import {
  $DatePickerInputAdapter,
  $DatePickerInputAdapterProps,
} from './adapters/date-picker-input';
import {
  $DateTimePickerAdapter,
  $DateTimePickerAdapterProps,
} from './adapters/date-time-picker';
import {
  $FileButtonAdapter,
  $FileButtonAdapterProps,
} from './adapters/file-button';
import {
  $MultiSelectAdapter,
  $MultiSelectAdapterProps,
} from './adapters/multi-select';
import {
  $NumberInputAdapter,
  $NumberInputAdapterProps,
} from './adapters/number-input';
import {
  $PasswordInputAdapter,
  $PasswordInputAdapterProps,
} from './adapters/password-input';
import {
  $PhoneNumberInputAdapter,
  $PhoneNumberInputAdapterProps,
} from './adapters/phone-number-input';
import {$PinInputAdapter, $PinInputAdapterProps} from './adapters/pin-input';
import {$RadioAdapter, $RadioAdapterProps} from './adapters/radio';
import {
  $RadioGroupAdapter,
  $RadioGroupAdapterProps,
  $RadioGroupItemAdapter,
} from './adapters/radio-group';
import {$RatingAdapter, $RatingAdapterProps} from './adapters/rating';
import {
  $SegmentedControlAdapter,
  $SegmentedControlAdapterProps,
} from './adapters/segemented-control';
import {$SelectAdapter, $SelectAdapterProps} from './adapters/select';
import {$SliderAdapter, $SliderAdapterProps} from './adapters/slider';
import {$SwitchAdapter, $SwitchAdapterProps} from './adapters/switch';
import {$TextAreaAdapter, $TextAreaAdapterProps} from './adapters/text-area';
import {$TextInputAdapter, $TextInputAdapterProps} from './adapters/text-input';
import {$TimeInputAdapter, $TimeInputAdapterProps} from './adapters/time-input';
import {
  $MaskedInputAdapter,
  $MaskedInputAdapterProps,
} from './adapters/masked-input';
import {
  $ColorInputAdapter,
  $ColorInputAdapterProps,
} from './adapters/color-input';
import {DatePickerType} from '@mantine/dates';

export const createForm = <TFieldValues extends FieldValues>() => {
  return withStaticProperties($HookForm<TFieldValues>, {
    Checkbox: createField<TFieldValues, $CheckboxAdapterProps>((props) => (
      <$CheckboxAdapter {...props} />
    )),
    CheckboxGroup: withStaticProperties(
      createField<TFieldValues, $CheckboxGroupAdapterProps>((props) => (
        <$CheckboxGroupAdapter {...props} />
      )),
      {
        Item: $CheckboxGroupItemAdapter,
      }
    ),
    Chip: createField<TFieldValues, $ChipAdapterProps>((props) => (
      <$ChipAdapter {...props} />
    )),
    ChipGroup: withStaticProperties(
      createField<TFieldValues, $ChipGroupAdapterProps>((props) => (
        <$ChipGroupAdapter {...props} />
      )),
      {
        Item: $ChipGroupItemAdapter,
      }
    ),
    ColorInput: createField<TFieldValues, $ColorInputAdapterProps>((props) => (
      <$ColorInputAdapter {...props} />
    )),
    ColorPicker: createField<TFieldValues, $ColorPickerAdapterProps>(
      (props) => <$ColorPickerAdapter {...props} />
    ),
    CurrencyInput: createField<TFieldValues, $CurrencyInputAdapterProps>(
      (props) => <$CurrencyInputAdapter {...props} />
    ),
    DatePickerInput: createField<TFieldValues, $DatePickerInputAdapterProps>(
      <T extends DatePickerType>(
        props: RenderFieldProps<$DatePickerInputAdapterProps<T>>
      ) => <$DatePickerInputAdapter<T> {...props} />
    ),
    DateTimePicker: createField<TFieldValues, $DateTimePickerAdapterProps>(
      (props) => <$DateTimePickerAdapter {...props} />
    ),
    FileButton: createField<TFieldValues, $FileButtonAdapterProps>((props) => (
      <$FileButtonAdapter {...props} />
    )),
    MultiSelect: createField<TFieldValues, $MultiSelectAdapterProps>(
      (props) => <$MultiSelectAdapter {...props} />
    ),
    NumberInput: createField<TFieldValues, $NumberInputAdapterProps>(
      (props) => <$NumberInputAdapter {...props} />
    ),
    PasswordInput: createField<TFieldValues, $PasswordInputAdapterProps>(
      (props) => <$PasswordInputAdapter {...props} />
    ),
    PhoneNumberInput: createField<TFieldValues, $PhoneNumberInputAdapterProps>(
      (props) => <$PhoneNumberInputAdapter {...props} />
    ),
    PinInput: createField<TFieldValues, $PinInputAdapterProps>((props) => (
      <$PinInputAdapter {...props} />
    )),
    Radio: createField<TFieldValues, $RadioAdapterProps>((props) => (
      <$RadioAdapter {...props} />
    )),
    RadioGroup: withStaticProperties(
      createField<TFieldValues, $RadioGroupAdapterProps>((props) => (
        <$RadioGroupAdapter {...props} />
      )),
      {
        Item: $RadioGroupItemAdapter,
      }
    ),
    Rating: createField<TFieldValues, $RatingAdapterProps>((props) => (
      <$RatingAdapter {...props} />
    )),
    SegmentedControl: createField<TFieldValues, $SegmentedControlAdapterProps>(
      (props) => <$SegmentedControlAdapter {...props} />
    ),
    Select: createField<TFieldValues, $SelectAdapterProps>((props) => (
      <$SelectAdapter {...props} />
    )),
    Slider: createField<TFieldValues, $SliderAdapterProps>((props) => (
      <$SliderAdapter {...props} />
    )),
    Switch: createField<TFieldValues, $SwitchAdapterProps>((props) => (
      <$SwitchAdapter {...props} />
    )),
    TextArea: createField<TFieldValues, $TextAreaAdapterProps>((props) => (
      <$TextAreaAdapter {...props} />
    )),
    TextInput: createField<TFieldValues, $TextInputAdapterProps>((props) => (
      <$TextInputAdapter {...props} />
    )),
    TimeInput: createField<TFieldValues, $TimeInputAdapterProps>((props) => (
      <$TimeInputAdapter {...props} />
    )),
    MaskedInput: createField<TFieldValues, $MaskedInputAdapterProps>(
      (props) => <$MaskedInputAdapter {...props} />
    ),

    Actions: $FormActions,
    Container: $FormContainer,
    FieldArray: $FieldArray<TFieldValues>,
    Row: $FormRow,
    ServerError: $ServerError,
    Submit: $FormSubmit,
  });
};

const createField = <
  TFieldValues extends FieldValues,
  TAdapterProps extends AdapterProps<any>,
>(
  renderAdapter: (renderProps: RenderFieldProps<TAdapterProps>) => ReactElement
) => HookFormFieldFactory<TAdapterProps, TFieldValues>({renderAdapter});